import { useState } from 'react';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Container from '@cloudscape-design/components/container';
import Header from '@cloudscape-design/components/header';
import Button from '@cloudscape-design/components/button';
import Table from '@cloudscape-design/components/table';
import Box from '@cloudscape-design/components/box';
import Badge from '@cloudscape-design/components/badge';
import Modal from '@cloudscape-design/components/modal';
import Form from '@cloudscape-design/components/form';
import FormField from '@cloudscape-design/components/form-field';
import Input from '@cloudscape-design/components/input';
import Textarea from '@cloudscape-design/components/textarea';
import Select, { SelectProps } from '@cloudscape-design/components/select';
import Checkbox from '@cloudscape-design/components/checkbox';
import ButtonDropdown from '@cloudscape-design/components/button-dropdown';
import Link from '@cloudscape-design/components/link';
import {
  activitiesApi,
  CohortActivity,
  CreateActivityRequest,
  UpdateActivityRequest,
  ACTIVITY_TYPE_OPTIONS,
} from '../../api/activities';
import { useQuery } from '@tanstack/react-query';
import { queryKeys } from '../../lib/queryClient';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { ErrorAlert } from '../../components/ErrorAlert';
import { useIsProgramAdminOrOwner } from '../../hooks/usePermissions';

interface ActivitiesTabProps {
  cohortId: string;
  programId: string;
  mentorsEnabled?: boolean;
  cohortStartDate?: string;
  cohortEndDate?: string;
}

/** Calculate total cohort weeks from start/end dates. Falls back to 12. */
function calcCohortWeeks(startDate?: string, endDate?: string): number {
  if (!startDate || !endDate) return 12;
  const start = new Date(startDate).getTime();
  const end = new Date(endDate).getTime();
  if (isNaN(start) || isNaN(end) || end <= start) return 12;
  return Math.max(1, Math.ceil((end - start) / (7 * 24 * 60 * 60 * 1000)));
}

/** Format a week's date range, e.g. "May 1 – May 7" */
function weekDateRange(weekNum: number, cohortStartDate?: string): string {
  if (!cohortStartDate) return '';
  // Append T00:00:00 to force local-time parsing and avoid UTC midnight off-by-one
  const start = new Date(cohortStartDate + 'T00:00:00');
  const weekStart = new Date(start);
  weekStart.setDate(start.getDate() + (weekNum - 1) * 7);
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekStart.getDate() + 6);
  const fmt = (d: Date) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  return `${fmt(weekStart)} – ${fmt(weekEnd)}`;
}

/** Build week dropdown options */
function buildWeekOptions(totalWeeks: number): SelectProps.Option[] {
  return [
    { value: '', label: '— No week —' },
    ...Array.from({ length: totalWeeks }, (_, i) => ({
      value: String(i + 1),
      label: `Week ${i + 1}`,
    })),
  ];
}

/** Group activities into week buckets. Returns ordered week numbers and an "unassigned" bucket. */
function groupByWeek(activities: CohortActivity[]): {
  weeks: number[];
  byWeek: Record<number, CohortActivity[]>;
  unassigned: CohortActivity[];
} {
  const byWeek: Record<number, CohortActivity[]> = {};
  const unassigned: CohortActivity[] = [];

  for (const activity of activities) {
    if (activity.week != null) {
      if (!byWeek[activity.week]) byWeek[activity.week] = [];
      byWeek[activity.week].push(activity);
    } else {
      unassigned.push(activity);
    }
  }

  // Sort within each week by order
  const sortByOrder = (a: CohortActivity, b: CohortActivity) => a.order - b.order;
  for (const week of Object.keys(byWeek)) {
    byWeek[Number(week)].sort(sortByOrder);
  }
  unassigned.sort(sortByOrder);

  const weeks = Object.keys(byWeek).map(Number).sort((a, b) => a - b);
  return { weeks, byWeek, unassigned };
}

const EMPTY_FORM: CreateActivityRequest = {
  activityType: 'Training',
  title: '',
  description: '',
  linkUrl: '',
  requiresMentorApproval: false,
  requiredForGraduation: false,
  week: undefined,
};

export function ActivitiesTab({
  cohortId,
  programId,
  mentorsEnabled = false,
  cohortStartDate,
  cohortEndDate,
}: ActivitiesTabProps) {
  const isAdmin = useIsProgramAdminOrOwner(programId);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState<CohortActivity | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<CreateActivityRequest>(EMPTY_FORM);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');

  const totalWeeks = calcCohortWeeks(cohortStartDate, cohortEndDate);
  const weekOptions = buildWeekOptions(totalWeeks);

  const {
    data: activities,
    isLoading: activitiesLoading,
    error: activitiesError,
    refetch: refetchActivities,
  } = useQuery({
    queryKey: queryKeys.activities.list(cohortId),
    queryFn: () => activitiesApi.list(cohortId),
    enabled: !!cohortId,
  });

  const resetForm = () => { setFormData(EMPTY_FORM); setError(null); };

  const openAddModal = () => { resetForm(); setShowAddModal(true); };

  const handleAddActivity = async () => {
    if (!formData.title.trim()) { setError('Activity title is required'); return; }
    setIsSubmitting(true); setError(null);
    try {
      await activitiesApi.create(cohortId, formData);
      await refetchActivities();
      setShowAddModal(false); resetForm();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create activity');
    } finally { setIsSubmitting(false); }
  };

  const handleEditActivity = async () => {
    if (!selectedActivity) return;
    if (!formData.title.trim()) { setError('Activity title is required'); return; }
    setIsSubmitting(true); setError(null);
    try {
      const updateData: UpdateActivityRequest = {
        activityType: formData.activityType,
        title: formData.title,
        description: formData.description,
        linkUrl: formData.linkUrl || undefined,
        requiredForGraduation: formData.requiredForGraduation,
        week: formData.week ?? null,
        ...(mentorsEnabled && { requiresMentorApproval: formData.requiresMentorApproval }),
      };
      await activitiesApi.update(cohortId, selectedActivity.activityId, updateData);
      await refetchActivities();
      setShowEditModal(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update activity');
    } finally { setIsSubmitting(false); }
  };

  const handleDeleteActivity = async () => {
    if (!selectedActivity) return;
    setIsSubmitting(true); setError(null);
    try {
      await activitiesApi.delete(cohortId, selectedActivity.activityId);
      await refetchActivities();
      setShowDeleteModal(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete activity');
    } finally { setIsSubmitting(false); }
  };

  const openEditModal = (activity: CohortActivity) => {
    setSelectedActivity(activity);
    setFormData({
      activityType: activity.activityType,
      title: activity.title,
      description: activity.description,
      linkUrl: activity.linkUrl || '',
      requiresMentorApproval: activity.requiresMentorApproval,
      requiredForGraduation: activity.requiredForGraduation ?? false,
      week: activity.week,
    });
    setShowEditModal(true);
  };

  const openDeleteModal = (activity: CohortActivity) => {
    setSelectedActivity(activity);
    setDeleteConfirmText('');
    setShowDeleteModal(true);
  };

  if (activitiesLoading) return <LoadingSpinner message="Loading activities..." />;
  if (activitiesError) return <ErrorAlert error={activitiesError} />;

  const allActivities = activities || [];
  const { weeks, byWeek, unassigned } = groupByWeek(allActivities);
  const hasWeekTags = weeks.length > 0;

  // Column definitions for the admin activity table (no Week column — it's the section header)
  const columnDefs = [
    {
      id: 'title',
      header: 'Title',
      cell: (item: CohortActivity) => (
        <SpaceBetween size="xxs">
          <Box fontWeight="bold">{item.title}</Box>
          {item.description && (
            <Box color="text-body-secondary" fontSize="body-s">{item.description}</Box>
          )}
          {item.linkUrl && (
            <Link external href={item.linkUrl} fontSize="body-s">{item.linkUrl}</Link>
          )}
        </SpaceBetween>
      ),
      isRowHeader: true,
      verticalAlign: 'top' as const,
      minWidth: 200,
    },
    {
      id: 'type',
      header: 'Type',
      cell: (item: CohortActivity) => item.activityType,
      verticalAlign: 'top' as const,
      width: 120,
    },
    {
      id: 'flags',
      header: 'Required?',
      cell: (item: CohortActivity) => (
        item.requiredForGraduation ? <Badge color="blue">Required</Badge> : null
      ),
      verticalAlign: 'top' as const,
      width: 120,
    },
    ...(mentorsEnabled ? [{
      id: 'mentorApproval',
      header: 'Review?',
      cell: (item: CohortActivity) => (
        item.requiresMentorApproval ? <Badge>Required</Badge> : null
      ),
      verticalAlign: 'top' as const,
      width: 150,
    }] : []),    ...(isAdmin ? [{
      id: 'actions',
      header: '',
      cell: (item: CohortActivity) => (
        <ButtonDropdown
          variant="icon"
          items={[
            { id: 'edit', text: 'Edit' },
            { id: 'delete', text: 'Delete' },
          ]}
          ariaLabel="Activity actions"
          expandToViewport
          onItemClick={({ detail }) => {
            if (detail.id === 'edit') openEditModal(item);
            if (detail.id === 'delete') openDeleteModal(item);
          }}
        />
      ),
      verticalAlign: 'top' as const,
      width: 50,
    }] : []),
  ];

  const renderTable = (items: CohortActivity[]) => (
    <Table
      wrapLines
      trackBy="activityId"
      columnDefinitions={columnDefs}
      items={items}
      variant="borderless"
      loadingText="Loading activities"
      empty={<Box textAlign="center" color="inherit"><Box variant="p">No activities</Box></Box>}
    />
  );

  const renderWeekSection = (weekNum: number, items: CohortActivity[]) => {
    const dateRange = weekDateRange(weekNum, cohortStartDate);
    return (
      <Container
        key={weekNum}
        header={
          <Header
            variant="h3"
            description={dateRange || undefined}
            counter={`(${items.length})`}
          >
            Week {weekNum}
          </Header>
        }
      >
        {renderTable(items)}
      </Container>
    );
  };

  const renderFormBody = () => (
    <Form errorText={error || undefined}>
      <SpaceBetween size="m">
        <FormField label="Activity Type" stretch>
          <Select
            selectedOption={ACTIVITY_TYPE_OPTIONS.find((opt) => opt.value === formData.activityType) || null}
            onChange={({ detail }) =>
              setFormData({ ...formData, activityType: detail.selectedOption.value as any })
            }
            options={ACTIVITY_TYPE_OPTIONS}
            selectedAriaLabel="Selected"
          />
        </FormField>

        <FormField label="Week (optional)" description="Group this activity under a cohort week" stretch>
          <Select
            selectedOption={
              formData.week != null
                ? { value: String(formData.week), label: `Week ${formData.week}` }
                : { value: '', label: '— No week —' }
            }
            onChange={({ detail }) =>
              setFormData({
                ...formData,
                week: detail.selectedOption.value ? Number(detail.selectedOption.value) : undefined,
              })
            }
            options={weekOptions}
            selectedAriaLabel="Selected week"
          />
        </FormField>

        <FormField label="Title" stretch>
          <Input
            value={formData.title}
            onChange={({ detail }) => setFormData({ ...formData, title: detail.value })}
            placeholder="Enter activity title"
          />
        </FormField>

        <FormField label="Description" stretch>
          <Textarea
            value={formData.description}
            onChange={({ detail }) => setFormData({ ...formData, description: detail.value })}
            placeholder="Enter activity description"
            rows={4}
          />
        </FormField>

        <FormField label="Link/URL (optional)" stretch>
          <Input
            value={formData.linkUrl || ''}
            onChange={({ detail }) => setFormData({ ...formData, linkUrl: detail.value })}
            placeholder="https://example.com"
            type="url"
          />
        </FormField>

        <FormField stretch>
          <SpaceBetween size="xs">
            <Checkbox
              checked={formData.requiresMentorApproval}
              disabled={!mentorsEnabled}
              onChange={({ detail }) =>
                setFormData({ ...formData, requiresMentorApproval: detail.checked })
              }
            >
              Requires mentor review
            </Checkbox>
            <Checkbox
              checked={formData.requiredForGraduation ?? false}
              onChange={({ detail }) =>
                setFormData({ ...formData, requiredForGraduation: detail.checked })
              }
            >
              Required for graduation
            </Checkbox>
          </SpaceBetween>
        </FormField>
      </SpaceBetween>
    </Form>
  );

  return (
    <>
      <SpaceBetween size="l">
        {/* Top header with Add button */}
        <Header
          variant="h2"
          actions={
            isAdmin && (
              <Button iconName="add-plus" onClick={openAddModal}>
                Add Activity
              </Button>
            )
          }
        >
          Activities
        </Header>

        {allActivities.length === 0 ? (
          <Box textAlign="center" color="inherit">
            <Box variant="p">No activities defined for this cohort yet.</Box>
            <Box variant="p" color="text-body-secondary">
              Add activities to define requirements for participants to complete.
            </Box>
          </Box>
        ) : (
          <SpaceBetween size="l">
            {/* Unassigned activities first */}
            {unassigned.length > 0 && (
              <Container
                header={
                  <Header variant="h3" counter={`(${unassigned.length})`}>
                    {hasWeekTags ? 'Additional Activities' : 'Activities'}
                  </Header>
                }
              >
                {renderTable(unassigned)}
              </Container>
            )}

            {/* Weekly activities after */}
            {weeks.map(weekNum => renderWeekSection(weekNum, byWeek[weekNum]))}
          </SpaceBetween>
        )}
      </SpaceBetween>

      {/* Add Activity Modal */}
      <Modal
        visible={showAddModal}
        onDismiss={() => setShowAddModal(false)}
        header="Add Activity"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={() => setShowAddModal(false)}>Cancel</Button>
              <Button variant="primary" onClick={handleAddActivity} loading={isSubmitting}>Add Activity</Button>
            </SpaceBetween>
          </Box>
        }
      >
        {renderFormBody()}
      </Modal>

      {/* Edit Activity Modal */}
      <Modal
        visible={showEditModal}
        onDismiss={() => setShowEditModal(false)}
        header="Edit Activity"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={() => setShowEditModal(false)}>Cancel</Button>
              <Button variant="primary" onClick={handleEditActivity} loading={isSubmitting}>Save Changes</Button>
            </SpaceBetween>
          </Box>
        }
      >
        {renderFormBody()}
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        visible={showDeleteModal}
        onDismiss={() => setShowDeleteModal(false)}
        header="Delete Activity"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={() => setShowDeleteModal(false)}>Cancel</Button>
              <Button
                variant="primary"
                onClick={handleDeleteActivity}
                loading={isSubmitting}
                disabled={deleteConfirmText !== 'confirm'}
              >
                Delete
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <SpaceBetween size="m">
          {error && <ErrorAlert error={error} />}
          <Box>
            Are you sure you want to permanently delete <strong>{selectedActivity?.title}</strong>?
            This will remove all associated participant completion records and cannot be undone.
          </Box>
          <FormField label={<>Type <strong>confirm</strong> to proceed</>}>
            <Input
              value={deleteConfirmText}
              onChange={({ detail }) => setDeleteConfirmText(detail.value)}
              placeholder="confirm"
              autoComplete="off"
            />
          </FormField>
        </SpaceBetween>
      </Modal>
    </>
  );
}
